import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["referencedObjectType", "propertyName"];
import { useState, useCallback, useMemo, useEffect } from 'react';
import I18n from 'I18n';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Select } from 'foundations-components/transitional/components/Select';
import ListingLibFilterWrapper from 'framework-listing-lib/filters/ListingLibFilterWrapper';
import { isInOrNotInOperator } from '../utils/typeCheckUtils';
import useContentPlatformPropertiesConfiguration from '../hooks/useContentPlatformPropertiesConfiguration';
import { getPropertyOptionsAsSelectOptions } from '../utils/propertyOptionsUtils';
import styled from 'foundations-theming/utils';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import ReferenceLiteSearchSelect from 'reference-resolvers-lite/components/ReferenceLiteSearchSelect';
import { Text } from 'foundations-components/components/Text';
import { DynamicFilterIcon } from 'foundations-assets';
import { CONTENT_PLATFORM_PROPERTY_NAME } from 'content-management-data-lib';
import { jsx as _jsx } from "react/jsx-runtime";
const isDropdownFooterOptionChecked = (filter, dropdownFooterOption) => dropdownFooterOption && filter && isInOrNotInOperator(filter) && filter.values.includes(dropdownFooterOption.value) || false;
const getFormattedOptions = (selectOptions, disabled) => selectOptions.map(option => Object.assign({}, option, {
  disabled
}));
const getFilterOperatorData = (quickFilters, propertyName) => quickFilters.find(filter => filter.property === propertyName);
const PROPERTIES_WITH_ME_OPTION = [CONTENT_PLATFORM_PROPERTY_NAME.CREATED_BY_USER_ID, CONTENT_PLATFORM_PROPERTY_NAME.UPDATED_BY_USER_ID];
const ReferenceObjectTypeFilter = _ref => {
  let {
      referencedObjectType,
      propertyName
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const resolver = createStandardCachedResolver(referencedObjectType);
  if (!resolver) {
    return null;
  }
  const defaultOptions = PROPERTIES_WITH_ME_OPTION.includes(propertyName) ? [{
    text: I18n.text('content-management-listing-lib.filters.meFilter'),
    value: '__hs__ME',
    avatar: /*#__PURE__*/_jsx(Text, {
      as: "span",
      marginInlineEnd: "100",
      children: /*#__PURE__*/_jsx(DynamicFilterIcon, {
        alignWithText: true
      })
    })
  }] : [];
  return /*#__PURE__*/_jsx(ReferenceLiteSearchSelect, Object.assign({}, props, {
    options: defaultOptions,
    resolver: resolver
  }));
};
const QuickFilterSelectComponent = ({
  onFilterChange,
  propertyDefinition,
  isMultiSelect = true,
  quickFilters,
  getQuickFilterValue,
  referencedObjectType
}) => {
  const {
    propertyDefinitionMap
  } = useContentPlatformPropertiesConfiguration();
  const {
    name,
    label
  } = propertyDefinition;
  const {
    selectOptions,
    dropdownFooterOption
  } = getPropertyOptionsAsSelectOptions(propertyDefinitionMap, name);
  const filter = getFilterOperatorData(quickFilters, name);
  const [dropdownFooterOptionChecked, setDropdownFooterOptionChecked] = useState(isDropdownFooterOptionChecked(filter, dropdownFooterOption));
  useEffect(() => {
    if (!filter && dropdownFooterOptionChecked) {
      setDropdownFooterOptionChecked(false);
    }
  }, [filter, dropdownFooterOptionChecked]);

  // If the operator type is not supported in the quick filter then we don't
  // want to use the quick filter, it will show up in the advanced panel
  const value = getQuickFilterValue(name);
  const numberOfSelectedOptions = Array.isArray(value) ? value.length : dropdownFooterOptionChecked ? 1 : 0;
  const options = useMemo(() => getFormattedOptions(selectOptions, dropdownFooterOptionChecked), [dropdownFooterOptionChecked, selectOptions]);
  const renderButtonContent = useCallback(() => {
    const filterLabel = label || I18n.text(`content-management-listing-lib.properties.labels.${name}`);
    const labelText = numberOfSelectedOptions > 0 ? `${filterLabel} ${I18n.text('content-management-listing-lib.quickFilters.activeDisplayNewFormat.countOfSelectedFiltersInParentheses', {
      count: numberOfSelectedOptions
    })}` : filterLabel;
    return /*#__PURE__*/_jsx(TruncateString, {
      maxWidth: 120,
      children: labelText
    });
  }, [label, name, numberOfSelectedOptions]);
  const onChange = useCallback(({
    target: {
      value: newValue
    }
  }) => {
    onFilterChange(name, newValue);
  }, [onFilterChange, name]);
  const handleDropdownFooterOptionChange = useCallback(({
    target: {
      checked
    }
  }) => {
    setDropdownFooterOptionChecked(checked);
    onFilterChange(name, checked ? [dropdownFooterOption.value] : null);
  }, [onFilterChange, name, dropdownFooterOption]);
  const StyledDropdownFooterSpan = styled.span.withConfig({
    displayName: "QuickFilterSelectComponent__StyledDropdownFooterSpan",
    componentId: "w011u4-0"
  })(["font-size:", ";"], ({
    theme
  }) => theme.typography['body-100'].fontSize);
  const dropdownFooter = useMemo(() => {
    if (dropdownFooterOption) {
      return /*#__PURE__*/_jsx(Checkbox, {
        checked: dropdownFooterOptionChecked,
        onChange: handleDropdownFooterOptionChange,
        "data-test-id": "quickfilter-select-footer-checkbox",
        children: /*#__PURE__*/_jsx(StyledDropdownFooterSpan, {
          children: dropdownFooterOption.text
        })
      });
    }
    return null;
  }, [dropdownFooterOptionChecked, dropdownFooterOption, handleDropdownFooterOptionChange, StyledDropdownFooterSpan]);
  const isFilterApplied = value != null || dropdownFooterOptionChecked;
  const sharedProps = {
    key: dropdownFooterOptionChecked ? 'footerChecked' : 'all',
    dropdownFooter,
    'data-filter-name': name,
    'data-test-id': 'quickfilter-select',
    ButtonContent: renderButtonContent,
    buttonUse: 'transparent',
    anchorType: 'button',
    clearable: isMultiSelect,
    menuWidth: 'auto',
    multi: isMultiSelect,
    onChange,
    value
  };
  return /*#__PURE__*/_jsx(ListingLibFilterWrapper, {
    active: isFilterApplied,
    children: referencedObjectType ? /*#__PURE__*/_jsx(ReferenceObjectTypeFilter, Object.assign({}, sharedProps, {
      referencedObjectType: referencedObjectType,
      propertyName: name
    })) : /*#__PURE__*/_jsx(Select, Object.assign({}, sharedProps, {
      options: options
    }))
  });
};
export default QuickFilterSelectComponent;